import React from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '../../lib/utils'
import type { SlideData, SlideTransition } from '../../types'

export interface SlideProps {
  data: SlideData
  isActive: boolean
  transition?: SlideTransition
  direction?: 'forward' | 'backward'
  className?: string;
}

interface TransitionVariant {
  initial: Record<string, string | number>;
  animate: Record<string, string | number>;
  exit: Record<string, string | number>;
}

const getTransitionVariants = (transition: SlideTransition, direction: 'forward' | 'backward'): TransitionVariant => {
  const baseVariants: Record<SlideTransition, TransitionVariant> = {
    slide: {
      initial: { x: direction === 'forward' ? '100%' : '-100%', opacity: 0 },
      animate: { x: 0, opacity: 1 },
      exit: { x: direction === 'forward' ? '-100%' : '100%', opacity: 0 },
    },
    fade: {
      initial: { opacity: 0 },
      animate: { opacity: 1 },
      exit: { opacity: 0 },
    },
    zoom: {
      initial: { scale: 0.8, opacity: 0 },
      animate: { scale: 1, opacity: 1 },
      exit: { scale: 1.2, opacity: 0 },
    },
    flip: {
      initial: { rotateY: direction === 'forward' ? 90 : -90, opacity: 0 },
      animate: { rotateY: 0, opacity: 1 },
      exit: { rotateY: direction === 'forward' ? -90 : 90, opacity: 0 },
    },
    none: {
      initial: {},
      animate: {},
      exit: {},
    },
  }
  return baseVariants[transition]
}

const backgroundClasses: Record<string, string> = {
  'gradient-primary': 'gradient-bg-primary dark:from-primary-900 dark:via-primary-800 dark:to-primary-900',
  'gradient-secondary': 'gradient-bg-secondary dark:from-secondary-900 dark:via-secondary-800 dark:to-secondary-900',
  'gradient-accent': 'gradient-bg-accent dark:from-accent-900 dark:via-accent-800 dark:to-accent-900',
  'gradient-dark': 'gradient-bg-dark',
  white: 'bg-card',
  gray: 'bg-muted',
}

export const Slide: React.FC<SlideProps> = ({
  data,
  isActive,
  transition = 'slide',
  direction = 'forward',
  className,
}) => {
  const { background = 'white', layout = 'center', animation, component: Component, props: componentProps } = data

  const layoutClasses = {
    center: 'items-center justify-center text-center',
    left: 'items-center justify-start text-left',
    right: 'items-center justify-end text-right',
    top: 'items-start justify-center text-center',
    bottom: 'items-end justify-center text-center',
    full: 'items-stretch justify-stretch',
  }

  const variants = getTransitionVariants(transition, direction)

  return (
    <AnimatePresence mode="wait">
      {isActive && (
        <motion.section
          initial={variants.initial}
          animate={variants.animate}
          exit={variants.exit}
          transition={{ duration: 0.5, ease: 'easeInOut' }}
          className={cn(
            'absolute inset-0 flex flex-col overflow-hidden',
            backgroundClasses[background] || background,
            layoutClasses[layout],
            animation && `animate-${animation}`,
            className
          )}
        >
          {/* Render custom component or default content */}
          {Component ? (
            <div className="w-full h-full overflow-auto flex items-center justify-center">
              <Component {...componentProps} />
            </div>
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center overflow-auto px-6 py-6">
              <div className="max-w-full max-h-full flex flex-col items-center justify-center">
                {data.title && (
                  <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-4 sm:mb-6 text-foreground text-center px-2">
                    {data.title}
                  </h1>
                )}
                {data.subtitle && (
                  <h2 className="text-xl sm:text-2xl md:text-3xl text-muted-foreground mb-6 sm:mb-8 text-center px-2">
                    {data.subtitle}
                  </h2>
                )}
                {data.content && (
                  <div className="max-w-4xl text-foreground px-2 overflow-auto">
                    {data.content}
                  </div>
                )}
              </div>
            </div>
          )}
        </motion.section>
      )}
    </AnimatePresence>
  )
}
